import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { palette, typography } from '@versports/theme'
import { VerSportsLogo } from './VerSportsLogo'

/**
 * Identidad legal / institucional de VerSports, en un solo lugar. Lo consume
 * el modal "Acerca de" (`AboutButton`) y cualquier superficie que necesite
 * el email de contacto o las rutas de los documentos legales.
 *
 * `termsPath` / `dataPolicyPath` son rutas del web-app (servido en `/`), así
 * que desde el vr-app (servido en `/vr/`) también resuelven: mismo origen.
 *
 * `contactEmail` es la casilla institucional real de VerSports (Zoho, la
 * misma que es root de la cuenta AWS y remitente autenticado en SendGrid —
 * `vrs-docs/44`). Reemplazó al placeholder `contacto@ver-sports.com` el
 * 2026-09-09, junto con los textos legales v1.0: es la dirección a la que
 * `/legal/datos` manda a ejercer los derechos de la Ley 25.326, así que tiene
 * que ser una casilla que alguien lea.
 */
export const ABOUT = {
  company: 'VerSports LLC',
  year: 2026,
  contactEmail: 'info@ver-sports.com',
  humaiUrl: 'https://humai.lat',
  termsPath: '/legal/terminos',
  dataPolicyPath: '/legal/datos',
} as const

/**
 * Botón flotante "Acerca de" + modal. **Reemplaza al `Footer` global**
 * (decisión de mg, 2026-09-08: "saquemos el footer y dejemos un floating
 * button para un modal about"). Debe montarse en toda superficie de página
 * completa (web-app: `AppShell`, `AuthLayout`, los `Shell` ad-hoc de los
 * invites, `KnowLayout`, `AdminLayout`, `ClubPage`, `TabletLayout`; vr-app:
 * el lobby) — es el único lugar donde viven el © / ™, el contacto, el
 * "powered by Humai" y los links a Términos y Política de datos.
 *
 * Estilos inline sobre los tokens de `@versports/theme` (mismo criterio que
 * `NavBar`): se ve igual en las dos apps sin depender de la extracción de
 * clases de Tailwind.
 *
 * - `navOffset`: la superficie tiene el `NavBar` inferior fijo (~76 px), el
 *   botón sube para no quedar tapado.
 * - `buildHash`: el vr-app pasa `__BUILD_HASH__`; se muestra como
 *   `build <sha>` al pie del modal (dato de operador, checklist 41/45).
 * - `assetBase`: se reenvía tal cual a `VerSportsLogo`.
 */
export function AboutButton({
  assetBase,
  buildHash,
  navOffset = false,
}: {
  assetBase?: string
  buildHash?: string
  navOffset?: boolean
} = {}) {
  const [open, setOpen] = useState(false)
  const bottom = navOffset
    ? 'calc(88px + env(safe-area-inset-bottom))'
    : 'calc(16px + env(safe-area-inset-bottom))'

  return (
    <>
      <button
        type="button"
        aria-label="Acerca de VerSports"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          right: 'calc(16px + env(safe-area-inset-right))',
          bottom,
          zIndex: 45,
          width: 40,
          height: 40,
          borderRadius: 999,
          border: `1px solid ${palette.outline}`,
          background: `${palette.surfaceElevated}e6`,
          color: palette.textSecondary,
          boxShadow: `0 6px 24px ${palette.primary}33`,
          backdropFilter: 'blur(10px)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          fontFamily: typography.family,
        }}
      >
        <InfoIcon />
      </button>
      {open && (
        <AboutModal assetBase={assetBase} buildHash={buildHash} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="8" r="1.1" fill="currentColor" />
      <path d="M12 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

/**
 * El modal en sí. Exportado aparte para poder renderizarlo controlado (y
 * testear el markup sin el estado del botón). Cierra con Escape, con click
 * en el fondo y con el botón "Cerrar"; mientras está abierto bloquea el
 * scroll del documento.
 */
export function AboutModal({
  assetBase,
  buildHash,
  onClose,
}: {
  assetBase?: string
  buildHash?: string
  onClose: () => void
}) {
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(12, 9, 30, 0.62)',
        backdropFilter: 'blur(4px)',
        fontFamily: typography.family,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 420,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          padding: 24,
          borderRadius: 12,
          border: `1px solid ${palette.primary}55`,
          background: palette.surfaceElevated,
          color: palette.textSecondary,
          boxShadow: `0 0 42px ${palette.primary}1f`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          textAlign: 'center',
          fontSize: typography.fontSizePx.sm,
          lineHeight: 1.5,
        }}
      >
        <VerSportsLogo assetBase={assetBase} size="lg" />
        <h2
          id={titleId}
          style={{
            margin: 0,
            fontSize: typography.fontSizePx.md,
            fontWeight: typography.weight.bold,
            color: palette.textPrimary,
            letterSpacing: typography.tracking.wide,
            textTransform: 'uppercase',
          }}
        >
          Acerca de VerSports
        </h2>

        <p style={{ margin: 0 }}>
          © {ABOUT.year} {ABOUT.company}. VerSports™ es marca de {ABOUT.company}.
          Todos los derechos reservados.
        </p>

        <p style={{ margin: 0 }}>
          Contacto:{' '}
          <a href={`mailto:${ABOUT.contactEmail}`} style={linkStyle}>
            {ABOUT.contactEmail}
          </a>
        </p>

        <nav aria-label="Documentos legales" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 18px' }}>
          <a href={ABOUT.termsPath} target="_blank" rel="noreferrer noopener" style={linkStyle}>
            Términos y condiciones
          </a>
          <a href={ABOUT.dataPolicyPath} target="_blank" rel="noreferrer noopener" style={linkStyle}>
            Política de datos
          </a>
        </nav>

        <p style={{ margin: 0, color: palette.textMuted, fontSize: typography.fontSizePx.xs }}>
          powered by{' '}
          <a href={ABOUT.humaiUrl} target="_blank" rel="noreferrer noopener" style={{ ...linkStyle, color: palette.textMuted }}>
            Humai
          </a>
          {buildHash && (
            <>
              {' · '}
              <span style={{ fontFamily: typography.monoFamily }}>build {buildHash}</span>
            </>
          )}
        </p>

        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          style={{
            marginTop: 4,
            padding: '8px 18px',
            borderRadius: 8,
            border: `1px solid ${palette.outline}`,
            background: palette.surface,
            color: palette.textPrimary,
            fontFamily: typography.family,
            fontSize: typography.fontSizePx.sm,
            fontWeight: typography.weight.medium,
            cursor: 'pointer',
          }}
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}

const linkStyle: CSSProperties = {
  color: palette.textPrimary,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  textDecorationColor: `${palette.primary}99`,
}
